/**
 * X-Lite Integration Helper
 * Handles calling through X-Lite softphone application
 * Uses HTTP API endpoint: http://192.168.1.252/call.php?exten=7001&number=7582885143
 */

// Server Configuration
const SERVER_URL = 'http://192.168.1.252';
const CALL_ENDPOINT = '/call.php';

/**
 * Initiate a call using HTTP API endpoint
 * @param {string} phoneNumber - Phone number to call (10 digits)
 * @param {string} extension - Employee extension (e.g., '7001')
 * @returns {Promise<boolean>} - Returns true if call was initiated
 */
export const initiateXliteCall = async (phoneNumber, extension = '') => {
  try {
    // Clean the phone number (remove spaces, dashes, etc.)
    let cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Remove leading 0 if present (common in Indian numbers)
    if (cleanPhone.startsWith('0')) {
      cleanPhone = cleanPhone.substring(1);
    }
    
    // Build the API URL
    // Format: http://192.168.1.252/call.php?exten=7001&number=7582885143
    const apiUrl = `${SERVER_URL}${CALL_ENDPOINT}?exten=${encodeURIComponent(extension)}&number=${encodeURIComponent(cleanPhone)}`;
    
    console.log('Initiating call via API:', apiUrl);
    
    // Method 1: Use fetch API (recommended)
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'no-cors', // Bypass CORS if server doesn't allow cross-origin
        cache: 'no-cache'
      });
      
      // With no-cors mode, we can't read the response, but the request is sent
      console.log('Call API request sent successfully');
      return true;
    } catch (fetchError) {
      console.warn('Fetch API failed, trying alternative method:', fetchError);
    }
    
    // Method 2: Use XMLHttpRequest as fallback
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', apiUrl, true);
      xhr.timeout = 5000; // 5 second timeout
      
      xhr.onload = () => {
        console.log('Call API request completed');
      };
      
      xhr.onerror = () => {
        console.warn('Call API request failed');
      };
      
      xhr.send();
      return true;
    } catch (xhrError) {
      console.warn('XMLHttpRequest failed, trying window.open:', xhrError);
    }
    
    // Method 3: Use window.open (fallback - opens in new tab/window)
    try {
      // Open in a hidden window to trigger the API call
      const callWindow = window.open(apiUrl, '_blank', 'width=1,height=1,left=-1000,top=-1000');
      
      // Close the window after a short delay
      setTimeout(() => {
        if (callWindow && !callWindow.closed) {
          callWindow.close();
        }
      }, 1000);
      
      return true;
    } catch (openError) {
      console.error('All call methods failed:', openError);
      alert(`Unable to initiate call.\n\nAPI URL: ${apiUrl}\n\nPlease check:\n1. Server is accessible: ${SERVER_URL}\n2. X-Lite is running\n3. Network connection is active`);
      return false;
    }
  } catch (error) {
    console.error('Error initiating call:', error);
    alert(`Error initiating call: ${error.message}\n\nPlease ensure the server is accessible and X-Lite is configured.`);
    return false;
  }
};

/**
 * End/Hangup a call by deleting the session using HTTP API endpoint
 * Uses DELETE method or action parameter to terminate the call session
 * @param {string} extension - Employee extension (e.g., '7001')
 * @param {string} phoneNumber - Phone number that was called (optional, for session identification)
 * @returns {Promise<boolean>} - Returns true if hangup was initiated
 */
export const endXliteCall = async (extension = '', phoneNumber = '') => {
  try {
    // Build the API URL - same endpoint as call initiation but with DELETE method or action=end
    // Format: http://192.168.1.252/call.php?exten=7001&number=8349500903&action=end
    // Or use DELETE method: DELETE http://192.168.1.252/call.php?exten=7001&number=8349500903
    
    let apiUrl;
    if (phoneNumber) {
      // Clean the phone number
      let cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
      if (cleanPhone.startsWith('0')) {
        cleanPhone = cleanPhone.substring(1);
      }
      apiUrl = `${SERVER_URL}${CALL_ENDPOINT}?exten=${encodeURIComponent(extension)}&number=${encodeURIComponent(cleanPhone)}&action=end`;
    } else {
      // If no phone number, just use extension with action=end
      apiUrl = `${SERVER_URL}${CALL_ENDPOINT}?exten=${encodeURIComponent(extension)}&action=end`;
    }
    
    console.log('Ending call session via API:', apiUrl);
    
    // Method 1: Try DELETE method first
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      
      console.log('Call session DELETE request sent successfully');
      return true;
    } catch (deleteError) {
      console.warn('DELETE method failed, trying GET with action=end:', deleteError);
    }
    
    // Method 2: Use GET with action=end parameter
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      
      console.log('Call session end request sent successfully');
      return true;
    } catch (fetchError) {
      console.warn('Fetch API failed, trying XMLHttpRequest:', fetchError);
    }
    
    // Method 3: Use XMLHttpRequest with DELETE
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('DELETE', apiUrl, true);
      xhr.timeout = 5000;
      
      xhr.onload = () => {
        console.log('Call session DELETE request completed');
      };
      
      xhr.onerror = () => {
        console.warn('Call session DELETE request failed');
      };
      
      xhr.send();
      return true;
    } catch (xhrDeleteError) {
      console.warn('XMLHttpRequest DELETE failed, trying GET:', xhrDeleteError);
    }
    
    // Method 4: Use XMLHttpRequest with GET
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', apiUrl, true);
      xhr.timeout = 5000;
      
      xhr.onload = () => {
        console.log('Call session end request completed');
      };
      
      xhr.onerror = () => {
        console.warn('Call session end request failed');
      };
      
      xhr.send();
      return true;
    } catch (xhrError) {
      console.warn('XMLHttpRequest GET failed, trying window.open:', xhrError);
    }
    
    // Method 5: Use window.open as last resort
    try {
      const hangupWindow = window.open(apiUrl, '_blank', 'width=1,height=1,left=-1000,top=-1000');
      setTimeout(() => {
        if (hangupWindow && !hangupWindow.closed) {
          hangupWindow.close();
        }
      }, 1000);
      return true;
    } catch (openError) {
      console.error('All hangup methods failed:', openError);
      alert(`Unable to end call session.\n\nPlease check:\n1. Server is accessible: ${SERVER_URL}\n2. X-Lite is running\n3. Network connection is active`);
      return false;
    }
  } catch (error) {
    console.error('Error ending call:', error);
    alert(`Error ending call: ${error.message}\n\nPlease ensure the server is accessible and X-Lite is configured.`);
    return false;
  }
};

/**
 * Check if X-Lite protocol is supported
 * @returns {boolean}
 */
export const isXliteSupported = () => {
  // Check if callto: protocol is available
  // This is a basic check - actual support depends on X-Lite installation
  return true; // Assume supported if X-Lite is installed
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digits
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Format Indian number (10 digits)
  if (cleaned.length === 10) {
    return `${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
  }
  
  return phoneNumber;
};

